import { useCallback, useContext, useMemo } from "react";
import { useLocation, useNavigate } from "react-router";

import { styled, useMediaQuery } from "@mui/material";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import { AppContext } from "App";
import { useSnackbar } from "notistack";

import { Logo } from "../Icons/Logo";
import CollapsedMenuContent from "./components/CollapsedMenuContent";
import DrawerToggleButton from "./components/DrawerToggleButton";
// Components
import ExpandedMenuContent from "./components/ExpandedMenuContent";
import { MenuPopper } from "./components/MenuPopper";
import { PaymentDialog } from "./components/PaymentDialog";
import {
  NAVIGATION_MENU_DRAWER_WIDTH,
  NAVIGATION_MENU_DRAWER_WIDTH_COLLAPSED,
  NAVIGATION_MENU_DRAWER_WIDTH_MOBILE,
} from "./config/constants";
import { membersMenuItem, menuConfig } from "./config/menuConfig";
// Hooks
import { useDrawer } from "./hooks/useDrawer";
import { useModuleNavigation } from "./hooks/useModuleNavigation";
import { usePaymentHandling } from "./hooks/usePaymentHandling";
import { useSubscription } from "./hooks/useSubscription";
import { useTooltipMenu } from "./hooks/useTooltipMenu";
import { useUser } from "./hooks/useUser";

export default function NavigationMenu() {
  const { globalState, globalDispatch } = useContext(AppContext);

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("md"), { noSsr: true });
  const isPrint = useMediaQuery("print");

  const { enqueueSnackbar } = useSnackbar();
  const handleError = useCallback(
    (err) => {
      console.warn(err);
      enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
    },
    [enqueueSnackbar]
  );

  // Hooks
  const { handleDrawerClose, handleDrawerOpen } = useDrawer();

  const { myUser, activeProducer, activeAgent, calculateIsModuleAllowed, handleLogoutClick } = useUser(
    {
      user_role: globalState.user_role,
      producer_token: globalState.producer_token,
      agent_token: globalState.agent_token,
      globalDispatch,
    },
    handleError
  );

  const {
    calculateIsSubMenuOpen,
    toggleIsSubMenuOpen,
    handleNavigate: baseHandleNavigate,
  } = useModuleNavigation(navigate, pathname);

  const handleNavigate = useCallback(
    async (path) => {
      baseHandleNavigate(path);
      if (isSmall) {
        handleDrawerClose();
      }
    },
    [baseHandleNavigate, isSmall, handleDrawerClose]
  );

  const { hoveredItem, tooltipAnchorEl, handleMenuItemHover, handleMenuItemLeave, handlePopperMouseEnter } =
    useTooltipMenu(isSmall, globalState.isMenuOpen);

  // Subscription
  const subscriptionInput = useMemo(() => {
    return {
      activeProducer,
      activeAgent,
      globalState: {
        producer_token: globalState.producer_token,
        subscription_payment_timestamp: globalState.subscription_payment_timestamp,
        subscription_snooze_timestamp: globalState.subscription_snooze_timestamp,
      },
    };
  }, [
    activeProducer?.id,
    activeProducer?.subscription_is_posted,
    activeAgent?.id,
    activeAgent?.subscription_is_posted,
    globalState.producer_token,
    globalState.subscription_payment_timestamp,
    globalState.subscription_snooze_timestamp,
  ]);

  const {
    openInvoices,
    doShowPaymentDialog,
    isLoadingPaymentLink,
    isLoadingPaperCheck,
    setDoShowPaymentDialog,
    setIsLoadingPaymentLink,
    setIsLoadingPaperCheck,
  } = useSubscription(subscriptionInput.activeProducer || subscriptionInput.activeAgent, subscriptionInput);

  const {
    handlePaymentSnooze,
    handlePayment,
    handlePaperCheck,
    handleActiveUserSignoutClick: baseHandleActiveUserSignoutClick,
  } = usePaymentHandling(
    activeProducer,
    activeAgent,
    {
      agent_token: globalState.agent_token,
      producer_token: globalState.producer_token,
      user_role: globalState.user_role,
      is_broker: globalState.is_broker,
    },
    globalDispatch,
    handleError,
    setDoShowPaymentDialog,
    setIsLoadingPaymentLink,
    setIsLoadingPaperCheck
  );

  const handleActiveUserSignoutClick = useCallback(() => {
    const path = baseHandleActiveUserSignoutClick();
    navigate(path);
  }, [baseHandleActiveUserSignoutClick, navigate]);

  // Styled components
  const DrawerHeader = styled("div")(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 1),
    //...theme.mixins.toolbar,
    justifyContent: "flex-end",
  }));

  // Render
  if (isPrint) {
    return null;
  }

  return (
    <>
      <Drawer
        sx={{
          width: [
            NAVIGATION_MENU_DRAWER_WIDTH_MOBILE,
            globalState?.isMenuOpen ? NAVIGATION_MENU_DRAWER_WIDTH : NAVIGATION_MENU_DRAWER_WIDTH_COLLAPSED,
          ],
          transition: (theme) => theme.transitions.create("width"),
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: [
              NAVIGATION_MENU_DRAWER_WIDTH_MOBILE,
              globalState?.isMenuOpen ? NAVIGATION_MENU_DRAWER_WIDTH : NAVIGATION_MENU_DRAWER_WIDTH_COLLAPSED,
            ],
            transition: (theme) => theme.transitions.create("width"),
            boxSizing: "border-box",
            border: "none",
            overflowX: "hidden",
            zIndex: 1,
          },
          border: "none",
        }}
        ModalProps={{ onClose: handleDrawerClose }}
        variant={isSmall ? "temporary" : "permanent"}
        anchor="left"
        open={isSmall ? globalState?.isMenuOpen : true}
      >
        <DrawerHeader sx={{ p: 0, m: 0 }}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{
              width: "100%",
              m: 0,
              position: "absolute",
              top: 0,
              zIndex: 1,
              height: "160px",
              borderBottomLeftRadius: "1.5rem",
              borderBottomRightRadius: "1.5rem",
              backgroundColor: (theme) => theme.palette.primary.main,
            }}
          >
            <a href="/">
              <Logo isCompact={!globalState?.isMenuOpen} />
            </a>
          </Box>
        </DrawerHeader>
        <Box
          className="menu-content"
          sx={{
            overflowY: "auto",
            height: "100%",
            position: "relative",
            mt: "140px",
            pt: "20px",
            ml: "10px",
            scrollbarGutter: "stable",
          }}
        >
          {isSmall || globalState?.isMenuOpen ? (
            <ExpandedMenuContent
              menuConfig={menuConfig}
              membersMenuItem={membersMenuItem}
              pathname={pathname}
              handleNavigate={handleNavigate}
              calculateIsModuleAllowed={calculateIsModuleAllowed}
              calculateIsSubMenuOpen={calculateIsSubMenuOpen}
              toggleIsSubMenuOpen={toggleIsSubMenuOpen}
              globalState={globalState}
              activeProducer={activeProducer}
              myUser={myUser}
              activeAgent={activeAgent}
              handleActiveUserSignoutClick={handleActiveUserSignoutClick}
              handleLogoutClick={handleLogoutClick}
            />
          ) : (
            <CollapsedMenuContent
              menuConfig={menuConfig}
              hoveredItem={hoveredItem}
              membersMenuItem={membersMenuItem}
              pathname={pathname}
              handleNavigate={handleNavigate}
              calculateIsModuleAllowed={calculateIsModuleAllowed}
              handleMenuItemHover={handleMenuItemHover}
              handleMenuItemLeave={handleMenuItemLeave}
              globalState={globalState}
              activeProducer={activeProducer}
              handleActiveUserSignoutClick={handleActiveUserSignoutClick}
              handleLogoutClick={handleLogoutClick}
            />
          )}
        </Box>
      </Drawer>

      <MenuPopper
        hoveredItem={hoveredItem}
        tooltipAnchorEl={tooltipAnchorEl}
        handlePopperMouseEnter={handlePopperMouseEnter}
        handleMenuItemLeave={handleMenuItemLeave}
        handleNavigate={handleNavigate}
        globalState={globalState}
        calculateIsModuleAllowed={calculateIsModuleAllowed}
      />

      <DrawerToggleButton
        isSmall={isSmall}
        isMenuOpen={globalState?.isMenuOpen}
        handleDrawerClose={handleDrawerClose}
        handleDrawerOpen={handleDrawerOpen}
      />

      <PaymentDialog
        open={doShowPaymentDialog}
        onClose={() => setDoShowPaymentDialog(false)}
        onSnooze={handlePaymentSnooze}
        onPayment={handlePayment}
        onPaperCheck={handlePaperCheck}
        openInvoices={openInvoices}
        isLoadingPaymentLink={isLoadingPaymentLink}
        isLoadingPaperCheck={isLoadingPaperCheck}
      />
    </>
  );
}
